/**
 * @typedef {Object} Action
 * @property {string} type
 * @property {string|boolean|number} payload
 */

export const ActionsEnum = {
  // --------------------------------------------------
  // Atomic actions
  // --------------------------------------------------
  SET_TTS_AVAILABLE: 'SET_TTS_AVAILABLE',
  SET_SPEAKING: 'SET_SPEAKING',
  SET_PAUSED: 'SET_PAUSED',
  SET_HAS_ANY_TEXT: 'SET_HAS_ANY_TEXT',
  SET_HAS_PREVIOUS: 'SET_HAS_PREVIOUS',
  SET_HAS_NEXT: 'SET_HAS_NEXT',
  SET_TTS_DIALOG_OPEN: 'SET_TTS_DIALOG_OPEN',
  SET_DID_CLICK_TTS_DIALOG_FIT: 'SET_DID_CLICK_TTS_DIALOG_FIT',
  SET_READ_FROM: 'SET_READ_FROM',
  // --------------------------------------------------
  // Composite actions
  // --------------------------------------------------
  SET_PREVIOUS_AND_NEXT: 'SET_PREVIOUS_AND_NEXT',
  START_READING: 'START_READING',
  PAUSE_READING: 'PAUSE_READING',
  STOP_READING: 'STOP_READING',
  // --------------------------------------------------
  // Reset actions
  // --------------------------------------------------
  RESET_STATE: 'RESET_STATE',
};

// ----------------------------------------------------------------------------------------------------
// Atomic actions
// ----------------------------------------------------------------------------------------------------

/**
 * @param {boolean} isTTSAvailable
 * @returns {Action}
 */
export function setTTSAvailable(isTTSAvailable) {
  return {
    type: ActionsEnum.SET_TTS_AVAILABLE,
    payload: isTTSAvailable ?? false,
  };
}

/**
 * @param {boolean} isSpeaking
 * @returns {Action}
 */
export function setSpeaking(isSpeaking) {
  return {
    type: ActionsEnum.SET_SPEAKING,
    payload: isSpeaking ?? false,
  };
}

/**
 * @param {boolean} isPaused
 * @returns {Action}
 */
export function setPaused(isPaused) {
  return {
    type: ActionsEnum.SET_PAUSED,
    payload: isPaused ?? false,
  };
}

/**
 * @param {boolean} hasAnyText
 * @returns {Action}
 */
export function setHasAnyText(hasAnyText) {
  return {
    type: ActionsEnum.SET_HAS_ANY_TEXT,
    payload: hasAnyText ?? false,
  };
}

/**
 * @param {boolean} hasPrevious
 * @returns {Action}
 */
export function setHasPrevious(hasPrevious) {
  return {
    type: ActionsEnum.SET_HAS_PREVIOUS,
    payload: hasPrevious ?? false,
  };
}

/**
 * @param {boolean} hasNext
 * @returns {Action}
 */
export function setHasNext(hasNext) {
  return {
    type: ActionsEnum.SET_HAS_NEXT,
    payload: hasNext ?? false,
  };
}

/**
 * @param {boolean} isOpen
 * @returns {Action}
 */
export function setTTSDialogOpen(isOpen) {
  return {
    type: ActionsEnum.SET_TTS_DIALOG_OPEN,
    payload: isOpen ?? false,
  };
}

/**
 * @param {boolean} didClick
 * @returns {Action}
 */
export function setDidClickTTSDialogFit(didClick) {
  return {
    type: ActionsEnum.SET_DID_CLICK_TTS_DIALOG_FIT,
    payload: didClick ?? false,
  };
}

/**
 * @param {string|null} readFrom
 * @returns {Action}
 */
export function setReadFrom(readFrom) {
  return {
    type: ActionsEnum.SET_READ_FROM,
    payload: readFrom,
  };
}

// ----------------------------------------------------------------------------------------------------
// Composite actions
// ----------------------------------------------------------------------------------------------------

/**
 * @param {boolean} hasPrevious
 * @param {boolean} hasNext
 * @returns {Action}
 */
export function setPreviousAndNext(hasPrevious, hasNext) {
  return {
    type: ActionsEnum.SET_PREVIOUS_AND_NEXT,
    payload: { hasPrevious: hasPrevious ?? false, hasNext: hasNext ?? false },
  };
}

/**
 * @returns {Action}
 */
export function startReading() {
  return { type: ActionsEnum.START_READING };
}

/**
 * @returns {Action}
 */
export function pauseReading() {
  return { type: ActionsEnum.PAUSE_READING };
}

/**
 * @returns {Action}
 */
export function stopReading() {
  return { type: ActionsEnum.STOP_READING };
}

// ----------------------------------------------------------------------------------------------------
// Reset actions
// ----------------------------------------------------------------------------------------------------

/**
 * @returns {Action}
 */
export function resetState() {
  return { type: ActionsEnum.RESET_STATE };
}
